import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'lodash'
import { Icon } from '@iconify/react'

import { Row, Col, Typography, Input, Dropdown } from 'antd'
import ProjectMultiSelect from '../../components/ProjectMultiSelect'
import HeaderPrimaryButton from '../../components/HeaderPrimaryButton'
import { useDispatch, useSelector } from 'react-redux'

// Components
import ClientsList from './components/ClientsList'
import ClientsListTableView from './components/ClientsListTableView'
import CreateClientModal from './components/CreateClientModal'
import {
    clientsState,
    fetchClients,
    fetchProjectsForClientModal,
} from './clients.slice'
import { loginState } from '../login/login.slice'
import { getClientsAccess } from '../../helpers/reportsPermission'

// Styles
import styles from './clients.module.css'
// import stylesClientListModal from './components/CreateClientModal.module.css'

const { Title } = Typography

const STATUS_LABELS = {
    active: 'Active',
    archived: 'Archived',
}

const Clients = () => {
    const dispatch = useDispatch()
    const { userProfile } = useSelector(loginState)
    const clientsAccess = useMemo(
        () => getClientsAccess(userProfile),
        [userProfile]
    )
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState('create')
    const [editingClient, setEditingClient] = useState(null)
    const [search, setSearch] = useState('')
    const [apiSearch, setApiSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState(null)
    const [viewMode, setViewMode] = useState('card')
    const [projectFilterIds, setProjectFilterIds] = useState([])

    const {
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalListStatus,
    } = useSelector(clientsState)

    const includeDeleted = useMemo(() => {
        if (statusFilter === 'archived') return true
        if (statusFilter === 'active') return false
        return undefined
    }, [statusFilter])

    const statusMenu = useMemo(
        () => ({
            items: [
                { key: 'active', label: 'Active' },
                { key: 'archived', label: 'Archived' },
            ],
            onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation?.()
                setStatusFilter(key)
            },
        }),
        []
    )

    const filterTrigger = (label) => (
        <button type="button" className={styles.pillBtn}>
            <span className={styles.pillLabel}>{label}</span>
            <span className={styles.pillChevron}>
                <Icon icon="mdi:chevron-down" width={18} height={18} />
            </span>
        </button>
    )

    const clearStatusFilter = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        setStatusFilter(null)
    }, [])

    const debouncedSetApiSearch = useMemo(
        () =>
            debounce((val) => {
                setApiSearch(val)
            }, 350),
        []
    )

    useEffect(() => {
        return () => debouncedSetApiSearch.cancel()
    }, [debouncedSetApiSearch])

    const debouncedProjectSearch = useMemo(
        () =>
            debounce((value) => {
                dispatch(
                    fetchProjectsForClientModal({
                        skip: 0,
                        filter: value ? { search: value } : {},
                        limit: 10,
                        search: value,
                        sortField: 'name',
                        sortOrder: 'asc',
                    })
                )
            }, 300),
        [dispatch]
    )

    useEffect(() => {
        return () => debouncedProjectSearch.cancel()
    }, [debouncedProjectSearch])

    const requestProjectIds = useMemo(
        () => (projectFilterIds.length > 0 ? projectFilterIds : undefined),
        [projectFilterIds]
    )

    // Keep list in sync with search + archive filter
    useEffect(() => {
        dispatch(
            fetchClients({
                skip: 0,
                limit: 10,
                search: apiSearch,
                projectIds: requestProjectIds,
                includeDeleted,
            })
        )
    }, [dispatch, includeDeleted, apiSearch, requestProjectIds])

    const handleProjectFilterOpen = useCallback(
        (open) => {
            if (!open) return
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                    sortField: 'name',
                    sortOrder: 'asc',
                })
            )
        },
        [dispatch]
    )

    const handleProjectFilterScroll = useCallback(
        (e) => {
            const { target } = e
            if (
                target.scrollHeight - target.scrollTop <=
                    target.clientHeight + 50 &&
                clientModalIsNext &&
                clientModalListStatus !== 'loading'
            ) {
                const nextPage = clientModalProjectPage + 1
                const skip = (nextPage - 1) * 10
                dispatch(
                    fetchProjectsForClientModal({
                        skip,
                        filter: clientModalProjectSearch
                            ? { search: clientModalProjectSearch }
                            : {},
                        limit: 10,
                        search: clientModalProjectSearch,
                        sortField: 'name',
                        sortOrder: 'asc',
                    })
                )
            }
        },
        [
            dispatch,
            clientModalIsNext,
            clientModalListStatus,
            clientModalProjectPage,
            clientModalProjectSearch,
        ]
    )

    const handleCreateClient = () => {
        if (!clientsAccess.canEdit) return
        setModalMode('create')
        setEditingClient(null)
        setIsModalOpen(true)
    }

    const handleEditClient = (client) => {
        if (!clientsAccess.canEdit) return
        setModalMode('edit')
        setEditingClient(client || null)
        setIsModalOpen(true)
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
        setModalMode('create')
        setEditingClient(null)
    }

    return (
        <>
            <div className={styles.pageContainer}>
                {/* Page Header */}
                <div className={styles.pageHeader}>
                    <Row
                        justify="space-between"
                        align="middle"
                        wrap
                        gutter={[16, 12]}
                        style={{
                            borderBottom: '1px solid #e2e8f0',
                            padding: '14px 24px 14px 24px',
                        }}
                    >
                        <Col flex="none">
                            <Title level={3} className={styles.pageTitle}>
                                Clients
                            </Title>
                        </Col>
                        <Col flex="1 1 280px" style={{ minWidth: 0 }}>
                            <div className={styles.controls}>
                                <div className={styles.searchWrap}>
                                    <Input
                                        allowClear
                                        size="large"
                                        placeholder="Search client"
                                        className={styles.search}
                                        prefix={
                                            <Icon
                                                icon="mdi:magnify"
                                                width={18}
                                                height={18}
                                            />
                                        }
                                        onChange={(e) => {
                                            const val = e.target.value || ''
                                            setSearch(val)
                                            debouncedSetApiSearch(val)
                                        }}
                                        value={search}
                                    />
                                </div>

                                <div className={styles.filterTriggers}>
                                    <Dropdown
                                        menu={statusMenu}
                                        trigger={['click']}
                                        getPopupContainer={(n) =>
                                            n?.parentElement ?? document.body
                                        }
                                    >
                                        {statusFilter ? (
                                            <div
                                                className={
                                                    styles.statusFilterChip
                                                }
                                                role="group"
                                                aria-label={`Status filter: ${STATUS_LABELS[statusFilter]}`}
                                            >
                                                <span
                                                    className={
                                                        styles.statusFilterChipLabel
                                                    }
                                                >
                                                    {
                                                        STATUS_LABELS[
                                                            statusFilter
                                                        ]
                                                    }
                                                </span>
                                                <button
                                                    type="button"
                                                    className={
                                                        styles.statusFilterChipClear
                                                    }
                                                    onClick={clearStatusFilter}
                                                    aria-label="Clear status filter"
                                                >
                                                    <Icon
                                                        icon="mdi:close"
                                                        width={16}
                                                        height={16}
                                                    />
                                                </button>
                                            </div>
                                        ) : (
                                            filterTrigger('Status')
                                        )}
                                    </Dropdown>
                                </div>
                                <ProjectMultiSelect
                                    projects={clientModalProjectsListData}
                                    value={projectFilterIds}
                                    onChange={setProjectFilterIds}
                                    placeholder="Select Projects"
                                    showSearch
                                    filterOption={false}
                                    onSearch={debouncedProjectSearch}
                                    onPopupScroll={handleProjectFilterScroll}
                                    loading={
                                        clientModalListStatus === 'loading'
                                    }
                                    page={clientModalProjectPage}
                                    onOpenChange={handleProjectFilterOpen}
                                />

                                <div className={styles.viewToggle}>
                                    <button
                                        type="button"
                                        className={`${styles.viewToggleBtn} ${
                                            viewMode === 'card'
                                                ? styles.viewToggleBtnActive
                                                : ''
                                        }`}
                                        onClick={() => setViewMode('card')}
                                    >
                                        <Icon
                                            icon="mdi:view-grid-outline"
                                            width={16}
                                        />
                                        Card
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.viewToggleBtn} ${
                                            viewMode === 'list'
                                                ? styles.viewToggleBtnActive
                                                : ''
                                        }`}
                                        onClick={() => setViewMode('list')}
                                    >
                                        <Icon
                                            icon="mdi:format-list-bulleted"
                                            width={16}
                                        />
                                        List
                                    </button>
                                </div>

                                {clientsAccess.canEdit ? (
                                    <HeaderPrimaryButton
                                        onClick={handleCreateClient}
                                    >
                                        Create Client
                                    </HeaderPrimaryButton>
                                ) : null}
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Clients List */}
                <div
                    id="clients-scroll-container"
                    className={styles.clientsContent}
                >
                    {viewMode === 'card' ? (
                        <ClientsList
                            onEditClient={handleEditClient}
                            apiSearch={apiSearch}
                            includeDeleted={includeDeleted}
                            projectFilterIds={projectFilterIds}
                            canEdit={clientsAccess.canEdit}
                            canDelete={clientsAccess.canDelete}
                        />
                    ) : (
                        <ClientsListTableView
                            onEditClient={handleEditClient}
                            apiSearch={apiSearch}
                            includeDeleted={includeDeleted}
                            projectFilterIds={projectFilterIds}
                            canEdit={clientsAccess.canEdit}
                            canDelete={clientsAccess.canDelete}
                        />
                    )}
                </div>
            </div>

            {/* Create Client Modal */}
            <CreateClientModal
                open={isModalOpen}
                onCancel={handleModalClose}
                mode={modalMode}
                initialClient={editingClient}
            />
        </>
    )
}

export default Clients
